import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Command } from '@integrate/contracts';
import {
  closeMonthOf,
  countsInHeadcount,
  currentBucket,
  fmt,
  fmtMoney,
  isPlacedInTarget,
  previewPerimeterThreshold,
  type SeparationState,
  type ThresholdPreview,
} from '@integrate/core';
import { TargetProgress } from './TargetProgress.js';
import { StatTile } from '../ui/StatTile.js';
import { Section } from './sections.js';

/**
 * The Perimeter phase panel, ported from `p2PanelHtml` / `p2StatsHtml` / `wireP2Panel`
 * (orgchart.html:6068–6137).
 *
 * This is the phase where the carve-out gets its shape, and the whole control is one slider: everyone at
 * or above a dedication threshold is in. Two properties make that safe to drag around live, and both are
 * load-bearing rather than incidental:
 *
 *   · The rule only ever moves people it owns — the undecided, plus its own previous picks. An explicit
 *     Remain, Carve-out or No-longer-required is somebody's decision, and the slider never touches it.
 *   · Dragging computes, it does not write. `previewPerimeterThreshold` is pure and lives in core, so the
 *     numbers under the slider are the numbers the server will produce, computed here without asking it.
 *
 * The commit is one command on release, not one per pixel — the legacy app splits the slider's `input`
 * and `change` events for exactly this reason, and so does this. Without the split, dragging from 40% to
 * 70% would write seven changes to the audit trail and seven undo steps.
 */
export function PerimeterPanel({
  state,
  readOnly,
  scenarioId,
  selectMode,
  perimeterFrozen,
  canFreeze,
  onDispatch,
  onToggleSelectMode,
  onNext,
}: {
  state: SeparationState;
  readOnly: boolean;
  scenarioId: string;
  /** Selection mode is the caller's state — the chart, the toolbar and this panel share the one flag. */
  selectMode: boolean;
  /** The governance gate: while true, the rule controls freeze and the pill below says why. */
  perimeterFrozen: boolean;
  /** Only the owner declares or lifts the gate — the SteerCo's act, spoken through one role. */
  canFreeze: boolean;
  onDispatch(command: Command): Promise<boolean>;
  onToggleSelectMode(): void;
  onNext(): void;
}): JSX.Element | null {
  const threshold = state.design.perimeterThreshold;
  // The rule controls freeze with the perimeter; the server refuses them anyway, but a live slider
  // over a frozen perimeter would promise a change the next drag cannot make.
  const ruleLocked = readOnly || perimeterFrozen;
  // Employees only, and a dedication of 0 is data. A legacy non-numeric marker parses to a null
  // dedication and deliberately does not enable the rule.
  const anyDedication = state.employees.some((e) => e.dedication != null);

  /*
    The slider's own value while the user is moving it.

    `null` means "follow the design". It is set while dragging and held across the commit, because the
    commit refetches the whole design and the slider must not snap back to its old position for the
    duration of a round trip.
  */
  const [draft, setDraft] = useState<number | null>(null);
  const sliderValue = draft ?? (threshold != null ? Math.round(threshold * 100) : 70);

  /*
    TWO previews, which is not redundancy.

    The tiles and the delta rows follow the drag. The section's title suffix and its badge follow the
    DESIGN — the legacy panel recomputes only `#p2Stats` while dragging (`:6112`) and leaves the heading
    alone (`:6098`). That is the right split: a badge that moved with the thumb would say a perimeter
    exists at a threshold nobody has applied.
  */
  const livePreview = useMemo(
    () => previewPerimeterThreshold(state, draft != null ? draft / 100 : threshold),
    [state, draft, threshold],
  );
  const committedPreview = useMemo(
    () => previewPerimeterThreshold(state, threshold),
    [state, threshold],
  );
  // What clearing the rule would release. Not `committedPreview.exitIds`, which answers a different
  // question (who the CURRENT rule would drop), and is not necessarily empty either.
  const clearedPreview = useMemo(() => previewPerimeterThreshold(state, null), [state]);

  /*
    Only the newest commit releases the draft.

    Two commits in quick succession resolve in an order nobody controls, and without the token a stale
    resolution clears the newer draft and the thumb jumps back to a value the user has already left.
  */
  const commitToken = useRef(0);
  /*
    One release, one command.

    Letting go of the thumb produces both a `pointerup` and a `change`, in an order the spec does not fix
    — and when no rule has been applied yet, both of them mean "apply this". Deduplicating on the value
    rather than on a busy flag is what keeps a genuine second gesture from being swallowed while the
    first is still in flight.
  */
  const lastSent = useRef<number | null>(null);

  const commit = useCallback(
    async (raw: number): Promise<void> => {
      const value = Math.max(0, Math.min(100, Math.round(raw)));
      // A no-op command is still an event in everyone's audit trail and an entry in the undo stack.
      // Releasing the thumb where it already was is not a decision.
      if (threshold != null && value === Math.round(threshold * 100)) {
        setDraft(null);
        return;
      }
      if (lastSent.current === value) return;
      lastSent.current = value;

      const token = ++commitToken.current;
      setDraft(value);
      await onDispatch({ type: 'applyPerimeterThreshold', scenarioId, threshold: value / 100 });
      // Released on failure too — the slider snapping back to the committed value is the correct signal
      // that nothing was applied. `dispatch` resolves either way and surfaces its own error banner.
      if (commitToken.current === token) {
        setDraft(null);
        lastSent.current = null;
      }
    },
    [onDispatch, scenarioId, threshold],
  );

  /*
    Divergence: the suggested default is reachable in one gesture.

    With no rule the controls sit at 70 while the design says null, and the DOM `change` event does not
    fire when the released value equals the value at focus time — so in the shipped app, applying exactly
    70% requires first applying something else. Releasing the thumb when no rule exists is unambiguous:
    it means apply this.
  */
  const commitIfUnapplied = useCallback(() => {
    if (threshold == null && !ruleLocked) void commit(sliderValue);
  }, [threshold, ruleLocked, commit, sliderValue]);

  // Nothing to report on before there is a roster; the phase bar hides itself for the same reason.
  if (!state.employees.length) return null;

  return (
    <>
      {/*
        The governance gate, stated where the governed thing lives. The pill is not decoration: the
        one state that changes what every control on this panel does must be visible on the panel,
        not discovered one refusal banner at a time.
      */}
      {perimeterFrozen && (
        <p className="muted" data-testid="perimeter-frozen">
          <strong>Perimeter frozen.</strong> Changes to who transfers land only as proposals somebody
          else applies — the recorded exception.
          {canFreeze && (
            <>
              {' '}
              <button
                data-testid="unfreeze-perimeter"
                onClick={() => void onDispatch({ type: 'setPerimeterFrozen', scenarioId, frozen: false })}
              >
                Unfreeze
              </button>
            </>
          )}
        </p>
      )}
      {!perimeterFrozen && canFreeze && (
        <p className="muted small">
          <button
            data-testid="freeze-perimeter"
            onClick={() => void onDispatch({ type: 'setPerimeterFrozen', scenarioId, frozen: true })}
          >
            Freeze perimeter
          </button>{' '}
          — signed off at a SteerCo? Freezing makes later changes go through reviewed proposals.
        </p>
      )}

      <Section
        testId="perimeter-rule"
        title="Threshold rule"
      >
        {/* Always rendered, including the no-dedication case: it is the only statement of the rule's
            inclusive "at or above" semantics. */}
        <p className="muted">
          Everyone with a dedication <strong>at or above</strong> the threshold is in the carve-out
          perimeter. Drag to preview — the rule is applied when you release. Manual assignments always win
          over the rule.
        </p>

        {anyDedication ? (
          <>
            <div className="slider-row">
              <RangeCommit
                value={sliderValue}
                disabled={ruleLocked}
                onPreview={setDraft}
                onCommit={commit}
                onRelease={commitIfUnapplied}
              />
              <PercentBox
                value={sliderValue}
                disabled={ruleLocked}
                onPreview={setDraft}
                onCommit={commit}
                onRelease={commitIfUnapplied}
              />
              <span className="muted">%</span>
            </div>
            <div className="sp-row">
              <span className="k">Rule status</span>
              <span className="v" data-testid="rule-status">
                {threshold != null ? `active — ≥ ${Math.round(threshold * 100)}%` : 'not applied yet'}
              </span>
            </div>
            {threshold != null && (
              <button
                style={{ width: '100%', marginTop: 6 }}
                data-testid="clear-rule"
                disabled={ruleLocked}
                onClick={() => void onDispatch({ type: 'clearPerimeterThreshold', scenarioId })}
              >
                {/* The label promises the manual assignments survive; it does not say how many people
                    the rule is holding. Saying so is the difference between a reversible action and a
                    surprising one. */}
                Clear rule (keep manual assignments) · −{fmt(clearedPreview.exitIds.length)}
              </button>
            )}
          </>
        ) : (
          <p className="muted" data-testid="no-dedication">
            <strong>No dedication data</strong> — the threshold rule is disabled. Map a Dedication column
            (phase 1) or enter values per person, and designate the perimeter manually below meanwhile.
          </p>
        )}
      </Section>

      <Section
        testId="perimeter-stats"
        title={
          <>
            {/* "(preview)" only when there is dedication data and no rule yet: the slider is an offer. */}
            Perimeter{threshold != null || !anyDedication ? '' : ' (preview)'}{' '}
            <span className="pill" data-testid="perimeter-hc">
              {fmt(committedPreview.hc)}
            </span>
          </>
        }
      >
        <PerimeterStats state={state} preview={livePreview} />
      </Section>

      <Section
        testId="perimeter-manual"
        title="Manual designation"
      >
        <p className="muted">
          Click any person or group on the chart and assign the <strong>Carve-out</strong> bucket. Manual
          picks are never touched by the slider.
        </p>
        <button
          data-testid="perimeter-select-mode"
          aria-pressed={selectMode}
          style={{ width: '100%', marginTop: 8 }}
          onClick={onToggleSelectMode}
        >
          {selectMode ? '✓ Selection mode on' : '✓ Build a selection'}
        </button>
        <p className="muted">
          In selection mode a click adds somebody instead of opening their panel; Ctrl/Cmd/Shift-click works
          either way. Click a <strong>group</strong> to take its whole team, the head included, or the
          organisation&rsquo;s own card to take everyone. Then move them all at once from the selection
          panel — and note it moves the people <em>in this view</em>, so a filter narrows what a bulk
          touches and says by how much.
        </p>
      </Section>

      {state.targetEnabled && <TargetProgress state={state} />}

      <button className="primary" style={{ width: '100%' }} data-testid="perimeter-next" onClick={onNext}>
        Next: transfers &amp; Day 1 →
      </button>
    </>
  );
}

/* ------------------------------------------------------------------ */

/** The four tiles and three rows under the slider, ported from `p2StatsHtml` (`:6068`). */
function PerimeterStats({
  state,
  preview,
}: {
  state: SeparationState;
  preview: ThresholdPreview;
}): JSX.Element {
  // Guarded: an empty population would otherwise print "NaN% of 0 people".
  const pctOfOrg = preview.totalHc ? Math.round((preview.hc / preview.totalHc) * 100) : 0;

  /*
    What the delta rows do not say.

    Applying the rule runs through the same write path as a bucket change, which drops the whole decision
    row on the way out and the stranded plan on the way in — so a rule pick that somebody had marked as
    exiting at Day 2 loses that, and someone joining the carve-out loses their RemainCo mitigation plan.
    The shipped app reports only "+N in / −M out" and the loss is silent (`:3756`, `:3686`, `:3690`).
    Naming it before the release is the difference between a reversible action and a discovered one.
  */
  const losingDay2Exit = preview.exitIds.filter((id) => state.design.bucketMeta[id]?.day2Exit).length;
  const losingStrandedPlan = preview.enterIds.filter((id) => state.design.strandedPlan[id]).length;

  return (
    <>
      <div className="pp-stats">
        <StatTile
          label="In perimeter"
          value={fmt(preview.hc)}
          sub={`${pctOfOrg}% of ${fmt(preview.totalHc)} people`}
        />
        <StatTile label="Dedicated FTE" value={fmt(preview.dedicatedFte, 1)} sub="Σ FTE × dedication" />
        <StatTile label="Full FTE" value={fmt(preview.fte, 1)} sub="people transfer whole" />
        <StatTile label="Cost" value={fmtMoney(preview.cost)} sub="annual salaries" />
      </div>
      {(() => {
        /*
          The pension tripwire's on-screen half: the perimeter is where the transferring population
          is decided, so the count belongs beside the decision. Schemes transfer with people and
          nothing here prices them; the actuaries do — this line exists so they get the list early.
        */
        const carriers = state.employees.filter(
          (e) =>
            !e.isLeaver &&
            !e.openPosition &&
            !e.inactive &&
            e.pensionScheme != null &&
            String(e.pensionScheme).trim() !== '' &&
            state.design.buckets[e.id] === 'Carve-out',
        );
        const schemes = new Set(carriers.map((e) => String(e.pensionScheme)));
        return carriers.length > 0 ? (
          <p className="muted small" data-testid="perimeter-pensions" data-n={carriers.length}>
            {fmt(carriers.length)} in the carve-out carry a pension scheme ({fmt(schemes.size)}{' '}
            {schemes.size === 1 ? 'scheme' : 'schemes'}) — flag to the actuaries before signing.
          </p>
        ) : null;
      })()}
      {(() => {
        /*
          Deferred closings, counted where the transferring population is decided: the perimeter
          says WHO transfers, the close grid says WHEN each country does, and a reader designing
          the perimeter should see that part of it moves months after Day 1.
        */
        const late = new Map<string, { month: number; n: number }>();
        for (const e of state.employees) {
          if (e.isLeaver || e.openPosition || e.inactive) continue;
          if (state.design.buckets[e.id] !== 'Carve-out') continue;
          const month = closeMonthOf(e, state.assumptions);
          if (month <= 0) continue;
          const key = String(e.country);
          const entry = late.get(key);
          if (entry) entry.n += 1;
          else late.set(key, { month, n: 1 });
        }
        const total = [...late.values()].reduce((a, x) => a + x.n, 0);
        const parts = [...late.entries()]
          .sort((a, b) => a[1].month - b[1].month || a[0].localeCompare(b[0]))
          .map(([country, { month, n }]) => `${country} M${month} (${fmt(n)})`)
          .join(', ');
        return total > 0 ? (
          <p className="muted small" data-testid="perimeter-deferred" data-n={total}>
            {fmt(total)} in the carve-out transfer after Day 1 — {parts} — deferred closings.
          </p>
        ) : null;
      })()}
      <div className="sp-row">
        <span className="k">Rule would add / remove</span>
        <span className="v" data-testid="rule-delta">
          +{fmt(preview.enterIds.length)} / −{fmt(preview.exitIds.length)}
        </span>
      </div>
      {(losingDay2Exit > 0 || losingStrandedPlan > 0) && (
        <p className="muted" data-testid="rule-collateral">
          {losingDay2Exit > 0 && (
            <>
              {fmt(losingDay2Exit)} of those removed{' '}
              {losingDay2Exit === 1 ? 'loses its' : 'lose their'} Day 2 exit.{' '}
            </>
          )}
          {losingStrandedPlan > 0 && (
            <>
              {fmt(losingStrandedPlan)} of those added{' '}
              {losingStrandedPlan === 1 ? 'loses its' : 'lose their'} RemainCo mitigation plan.
            </>
          )}
        </p>
      )}
      <div className="sp-row">
        <span className="k">Manual overrides kept</span>
        <span className="v" data-testid="manual-kept">
          {fmt(preview.manualIn)} in · {fmt(preview.manualOut)} out
        </span>
      </div>
      <div className="sp-row">
        <span className="k">No dedication data</span>
        <span className="v">{fmt(preview.missingDedication)}</span>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * The two inputs
 * ------------------------------------------------------------------ */

/**
 * Subscribe to a control's real `change` event.
 *
 * React normalises an input's `onChange` onto the DOM `input` event — every step of a drag, every
 * keystroke — and offers no hook for the DOM `change` event, which is the one that means "the user has
 * settled on this". That distinction is the entire design of this control (`wireP2Panel`: `input`
 * previews, `change` applies), so the real event is subscribed to directly rather than approximated with
 * pointer and key handlers that would each miss a case.
 */
function useNativeChange(
  ref: React.RefObject<HTMLInputElement>,
  handler: (el: HTMLInputElement) => void,
): void {
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onChange = (): void => latest.current(el);
    el.addEventListener('change', onChange);
    return () => el.removeEventListener('change', onChange);
  }, [ref]);
}

function RangeCommit({
  value,
  disabled,
  onPreview,
  onCommit,
  onRelease,
}: {
  value: number;
  disabled: boolean;
  onPreview(value: number): void;
  onCommit(value: number): void;
  onRelease(): void;
}): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  useNativeChange(ref, (el) => onCommit(Number(el.value)));

  return (
    <input
      ref={ref}
      className="threshold-slider"
      data-testid="threshold-slider"
      type="range"
      min={0}
      max={100}
      step={5}
      value={value}
      disabled={disabled}
      // Repainted from the value on every render; without it the track shows 0% at every position.
      style={{ ['--fill' as string]: `${value}%`, flex: 1 }}
      onChange={(e) => onPreview(Number(e.target.value))}
      // Releasing without having moved fires no `change`, which is why the default is otherwise
      // unreachable — see commitIfUnapplied.
      onPointerUp={onRelease}
      onKeyUp={onRelease}
    />
  );
}

/**
 * The percent box.
 *
 * Its text is local rather than derived, which is a deliberate divergence: the shipped app rewrites the
 * field from the clamped number on every keystroke (`:6110`, `:6124`), so deleting the contents writes a
 * `0` straight back into the field being edited, and `commit(+num.value || 0)` (`:6125`) then applies a
 * 0% rule — sweeping every undecided person with any dedication into the carve-out. Select-all, delete,
 * Tab does that, with no confirmation. Here an empty or unparseable field commits nothing and reverts,
 * while a deliberately typed `0` still applies 0%.
 */
function PercentBox({
  value,
  disabled,
  onPreview,
  onCommit,
  onRelease,
}: {
  value: number;
  disabled: boolean;
  onPreview(value: number): void;
  onCommit(value: number): void;
  onRelease(): void;
}): JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  const [text, setText] = useState<string | null>(null);

  useNativeChange(ref, (el) => {
    const raw = el.value.trim();
    const parsed = Number(raw);
    if (raw === '' || !Number.isFinite(parsed)) {
      // Nothing typed is not a threshold of nothing. Put the field back and say nothing to the server.
      setText(null);
      return;
    }
    setText(null);
    if (Math.max(0, Math.min(100, Math.round(parsed))) === value) onRelease();
    else onCommit(parsed);
  });

  return (
    <input
      ref={ref}
      data-testid="threshold-number"
      type="number"
      min={0}
      max={100}
      step={5}
      value={text ?? String(value)}
      disabled={disabled}
      onChange={(e) => {
        setText(e.target.value);
        const parsed = Number(e.target.value);
        // Preview what is typeable; leave a half-typed or empty field showing the design's number.
        if (e.target.value.trim() !== '' && Number.isFinite(parsed)) {
          onPreview(Math.max(0, Math.min(100, Math.round(parsed))));
        }
      }}
      onBlur={() => setText(null)}
    />
  );
}
